// Ring buffer of recent failed network calls (status >= 400 or thrown).
// Wraps fetch globally so any caller (apiService, third-party libs) is captured.

export interface NetworkEntry {
  method: string;
  url: string;
  status: number;
  durationMs?: number;
  responseSnippet?: string;
  timestamp: number;
}

const MAX_ENTRIES = 20;
let buffer: NetworkEntry[] = [];
let installed = false;

function push(entry: NetworkEntry): void {
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) buffer = buffer.slice(-MAX_ENTRIES);
}

export function installNetworkBuffer(): void {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    // Defensive: callers occasionally hand us undefined when an upstream call
    // (e.g. a presign API) returned a wrapped response with no usable URL.
    // Delegate to the original fetch so the real TypeError surfaces in *that*
    // call's stack rather than blowing up our wrapper.
    if (input == null) return originalFetch(input as any, init);
    const started = Date.now();
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : (input as any)?.url ?? '';
    const method = (
      init?.method ||
      (typeof input !== 'string' && !(input instanceof URL) ? (input as any)?.method : 'GET') ||
      'GET'
    ).toUpperCase();
    try {
      const res = await originalFetch(input, init);
      if (!res.ok) {
        let snippet = '';
        try {
          const clone = res.clone();
          snippet = (await clone.text()).slice(0, 240);
        } catch {
          // ignore
        }
        push({
          method,
          url,
          status: res.status,
          durationMs: Date.now() - started,
          responseSnippet: snippet,
          timestamp: Date.now(),
        });
      }
      return res;
    } catch (err) {
      push({
        method,
        url,
        status: 0,
        durationMs: Date.now() - started,
        responseSnippet: (err as Error)?.message?.slice(0, 240),
        timestamp: Date.now(),
      });
      throw err;
    }
  };
}

export function snapshotNetwork(maxLast = 10): NetworkEntry[] {
  return buffer.slice(-maxLast);
}

export function clearNetworkBuffer(): void {
  buffer = [];
}
